import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyName,
  usePropertyPhone,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { relativeLuminance, getContrastTextColor } from '../utils/luminance';
import { hasApartmentSyncMap, hasEngrainSitemap } from '../utils/interactiveMap';
import { isWashingtonState } from '../utils/washington';
// Base pages list from the parent aster theme, shallow-merged with this
// theme's `src/pages.json` overrides (e.g. Home visible, Future Residents
// Club). The runtime doesn't expose page metadata (position / parent /
// template) through a hook, and we can't reach into the parent's internals
// (parent-src imports are lint errors). Aster's package.json doesn't list
// `./pages.json` in its exports map, but the platform's Vite alias resolves
// the bare specifier at build time.
// @ts-expect-error — @vibesites/theme-aster/pages.json is resolved at build time
//              via the Vite alias.
import basePagesData from '@vibesites/theme-aster/pages.json';
import localPagesData from '../pages.json';

// ============================================================================
// Dynamic Navigation from pages.json
// ----------------------------------------------------------------------------
// This header intentionally re-implements the nav-page filter locally rather
// than importing a shared helper from the parent theme so the nav-content
// rules can match Weidner Blue Haven branding while the surrounding Aster
// header layout (utility bar, brand fonts, drawer) stays intact.
//
// Content differences from Aster's default:
//   • `home` is included as an explicit nav link (Weidner branding surfaces
//     Home in the row alongside Floor Plans / Amenities / ...).
//   • `contact` is included as a top-level nav link.
//   • `apply-now` and `resident-access` are excluded from the row — they
//     surface as utility / drawer CTAs.
//   • Three "synthetic" dropdowns group related pages under a single label:
//       - Find Your Home  → Floor Plans + Interactive Map
//       - Features        → Amenities + Pet Policy + Future Residents Club
//       - Gallery & Tours → Gallery + Virtual Tour + Schedule a Tour
//     Synthetic groups collapse to their single surviving child when the
//     property doesn't have the others (e.g. no interactive map).
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

interface NavLink {
  alias?: string;
  label: string;
  href?: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`, or
      when a synthetic group defines child aliases explicitly. */
  children?: NavLink[];
}

interface NavGroup {
  label: string;
  aliases: string[];
}

const NAV_EXCLUDED_ALIASES = new Set<string>([
  'privacy-policy',
  'sitemap',
  'accessibility',
  // Apply Now + Resident Access are header CTAs (not nav-row links).
  'apply-now',
  'thank-you',
  'resident-access',
  // `schedule-a-tour` and `pet-policy` participate via synthetic groups.
  'hyperlocal',
  'reviews',
  // Qualifying Criteria is WA-only and surfaces in the utility bar /
  // mobile drawer CTAs, not the main nav.
  'qualifying-criteria',
  // `contact` is intentionally NOT excluded — Contact Us is a top-level
  // nav link. Apply Now / Resident Access hold the CTA slot.
]);

const SYNTHETIC_NAV_GROUPS: NavGroup[] = [
  { label: 'Find Your Home', aliases: ['floor-plans', 'interactive-map'] },
  { label: 'Features', aliases: ['amenities', 'pet-policy', 'future-residents-club'] },
  { label: 'Gallery & Tours', aliases: ['gallery', 'virtual-tour', 'schedule-a-tour'] },
];

const SYNTHETIC_GROUP_ALIASES = new Set(SYNTHETIC_NAV_GROUPS.flatMap((group) => group.aliases));

/** Shallow-merge parent pages with this theme's pages.json overrides so nav
    sees local additions (Future Residents Club) and field tweaks (Home
    visible) the same way the platform page manifest does. */
function mergePagesData(
  base: Record<string, PageEntry>,
  local: Record<string, Partial<PageEntry>>,
): Record<string, PageEntry> {
  const merged: Record<string, PageEntry> = { ...base };
  for (const [alias, override] of Object.entries(local)) {
    merged[alias] = { ...(merged[alias] ?? {}), ...override } as PageEntry;
  }
  return merged;
}

const pagesData = mergePagesData(
  basePagesData as Record<string, PageEntry>,
  localPagesData as Record<string, Partial<PageEntry>>,
);

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface PageSection {
  slot: string;
}

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'HeroSolidBG';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 *
 * `hasInteractiveLeasing` gates the Interactive Map page — properties that
 * don't have an Engrain Sightmap ID, ApartmentSync config, or editor
 * override don't get the map page in the nav, and the synthetic Find Your
 * Home dropdown collapses to plain Floor Plans.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.visible === false) return false;
      if (page.template) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      if (alias === 'interactive-map' && !hasInteractiveLeasing) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];
  const consumedAliases = new Set<string>();

  // First pass: create a NavLink for every eligible page.
  for (const [alias, page] of eligible) {
    linksByAlias.set(alias, { alias, label: page.name, href: page.path });
  }

  // Second pass: attach any pages that still use a real pages.json `parent`.
  // Synthetic groups below own their aliases explicitly so they don't inherit
  // parent relationships like `interactive-map -> floor-plans`.
  for (const [alias, page] of eligible) {
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;
    if (page.parent && SYNTHETIC_GROUP_ALIASES.has(page.parent)) continue;
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    }
  }

  // Third pass: build the top-level nav in page position order, inserting
  // synthetic dropdowns where this Weidner header needs grouped labels that
  // don't correspond to real pages. Each synthetic group is ANCHORED to
  // the FIRST alias in its `aliases` array — that alias's page position is
  // what places the group in the nav row.
  const emittedGroups = new Set<NavGroup>();
  const emitSyntheticGroup = (group: NavGroup) => {
    if (emittedGroups.has(group)) return;
    emittedGroups.add(group);
    const children = group.aliases
      .map((childAlias) => linksByAlias.get(childAlias))
      .filter((link): link is NavLink => !!link);
    for (const child of children) {
      if (child.alias) consumedAliases.add(child.alias);
    }
    if (children.length === 1) {
      topLevel.push(children[0]);
    } else if (children.length > 1) {
      topLevel.push({ label: group.label, children });
    }
  };

  for (const [alias, page] of eligible) {
    if (consumedAliases.has(alias)) continue;

    const anchoredGroup = SYNTHETIC_NAV_GROUPS.find((item) => item.aliases[0] === alias);
    if (anchoredGroup) {
      emitSyntheticGroup(anchoredGroup);
      continue;
    }

    // Non-anchor member of a synthetic group: hold — don't emit it
    // standalone at this position.
    if (SYNTHETIC_GROUP_ALIASES.has(alias)) continue;

    const parent = page.parent;
    if (parent && eligibleAliases.has(parent) && parent !== alias) continue;

    topLevel.push(linksByAlias.get(alias)!);
  }

  // Fallback sweep: emit any synthetic group whose anchor alias was
  // filtered out entirely (e.g. `floor-plans` hidden on a property).
  for (const group of SYNTHETIC_NAV_GROUPS) {
    emitSyntheticGroup(group);
  }

  return topLevel.filter((link) => !link.children || link.children.length > 0);
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef: SlotGroupDefinition = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef: SlotGroupDefinition = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Qualifying Criteria link — WA-only. Sits in the desktop utility bar to
// the right of Resident Access (and as a drawer CTA on mobile). Pulled out
// of the main nav row and surfaced here per Weidner content rules.
const qualifyingCriteriaLinkDef: SlotGroupDefinition = {
  label: 'Qualifying Criteria Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/qualifying-criteria' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Schedule a Tour CTA — surfaces as a button on the right of the header
// row from tablet up (md+). On tablet it stands in for the phone icon; on
// desktop it occupies the right-hand column opposite the logo. Defaults
// mirror the parent theme's schedule-tour link (the `/schedule-a-tour`
// page) so editors can override the destination, label, and target
// independently.
const scheduleTourLinkDef: SlotGroupDefinition = {
  label: 'Schedule a Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address link — used in the desktop utility bar and the mobile drawer.
// Promoted from a bare `header.full_address` content token + auto-generated
// Google Maps directions URL to a slot group so editors can pick the address
// text, the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef: SlotGroupDefinition = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// ============================================================================
// Office hours (mobile drawer) — mirrors the Footer's office-hours column so
// the drawer's "Office Hours" block reads from the same `office_hours`
// collection and groups consecutive same-hours days into ranges.
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;
  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  // Index the incoming entries by normalized day. We iterate the canonical
  // week below rather than the raw list so that any day NOT pulling hours
  // through (missing entirely, or present but with no open/close time) is
  // still rendered — marked "Closed" — instead of silently dropped.
  const byDay = new Map<string, OfficeHour>();
  for (const entry of raw) {
    if (!hasValue(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (DAY_ORDER.includes(key) && !byDay.has(key)) {
      byDay.set(key, entry);
    }
  }

  const groups: GroupedHours[] = [];
  for (const key of DAY_ORDER) {
    const entry = byDay.get(key);
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed = !entry || (!hasValue(entry.open_time) && !hasValue(entry.close_time));
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} – ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const dash = prev.shortLabel.indexOf(' – ');
      const startShort = dash !== -1 ? prev.shortLabel.slice(0, dash) : prev.shortLabel;
      prev.shortLabel = `${startShort} – ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }
  return groups;
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Splits a single-line address into a street line and a "city, state zip" line
 * so the drawer address renders on two lines (matching the reference). Drops a
 * trailing country segment. Falls back to a single line when there are no commas.
 */
function splitAddressLines(full: string | null | undefined): { line1: string; line2: string } {
  const cleaned = (full || '')
    .trim()
    .replace(/,?\s*(usa|u\.s\.a\.|united states(?: of america)?)\.?\s*$/i, '')
    .trim();
  const parts = cleaned.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { line1: cleaned, line2: '' };
  return { line1: parts[0], line2: parts.slice(1).join(', ') };
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1.5 4.5a3 3 0 013-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 01-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 006.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 011.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 01-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5z"
      />
    </svg>
  );
}

// ============================================================================
// Mobile drawer nav item — plain link, or accordion for synthetic groups
// ============================================================================

function MobileNavItem({
  link,
  currentPath,
  onNavigate,
}: {
  link: NavLink;
  currentPath: string;
  onNavigate: () => void;
}) {
  const hasChildren = !!(link.children && link.children.length);
  const childActive =
    !!link.children?.some((c) => !!c.href && normalizePath(c.href) === currentPath);
  const isActive =
    (!!link.href && normalizePath(link.href) === currentPath) || childActive;
  // Auto-expand when the visitor is already on a child page so the active
  // sub-link is visible without an extra tap.
  const [open, setOpen] = useState(childActive);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        onClick={onNavigate}
        aria-current={isActive ? 'page' : undefined}
        className={`mobile-nav-link block py-3 text-[22px] leading-tight font-normal transition-colors ${
          isActive ? 'mobile-nav-active' : ''
        }`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div className="mobile-nav-group">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={`mobile-nav-link mobile-nav-toggle flex w-full items-center justify-between gap-3 py-3 text-left text-[22px] leading-tight font-normal bg-transparent transition-colors ${
          isActive ? 'mobile-nav-active' : ''
        }`}
      >
        <span>{link.label}</span>
        <ChevronDownIcon
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`mobile-nav-children overflow-hidden transition-[max-height,opacity] duration-300 ease-out ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        {link.children!.map((child) => {
          const childIsActive = !!child.href && normalizePath(child.href) === currentPath;
          return (
            <a
              key={child.href || child.label}
              href={child.href}
              onClick={onNavigate}
              aria-current={childIsActive ? 'page' : undefined}
              className={`mobile-nav-link mobile-nav-child block py-2.5 pl-1 text-[17px] leading-tight font-normal transition-colors ${
                childIsActive ? 'mobile-nav-active' : ''
              }`}
            >
              {child.label}
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

/** Normalize a pathname for active-link comparison: drop the query/hash and
    trailing slashes so `/floor-plans`, `/floor-plans/`, and
    `/floor-plans?x=1` all match the `/floor-plans` nav href. Root stays `/`. */
function normalizePath(p?: string | null): string {
  const path = (p || '').split(/[?#]/)[0];
  return path.replace(/\/+$/, '') || '/';
}

/** Brief grace period before a hover menu closes so the pointer can cross
    from the trigger into the panel without the menu vanishing. Long enough
    to cover a React paint: the panel is `pointer-events-none` until `open`
    commits, so a downward move can hit the page behind it and fire
    mouseleave before the submenu is hoverable. */
const NAV_DROPDOWN_CLOSE_MS = 120;

function useHoverMenu(containerRef?: { current: HTMLElement | null }) {
  const [open, setOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current != null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const openMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const closeMenu = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      // If the panel appeared under the pointer after mouseleave (no new
      // mouseenter), :hover is still true — keep the menu open.
      if (containerRef?.current?.matches(':hover')) {
        closeTimerRef.current = null;
        return;
      }
      setOpen(false);
      closeTimerRef.current = null;
    }, NAV_DROPDOWN_CLOSE_MS);
  }, [clearCloseTimer, containerRef]);

  const dismissMenu = useCallback(() => {
    clearCloseTimer();
    setOpen(false);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return { open, openMenu, closeMenu, dismissMenu };
}

function DesktopNavLink({ link, active = false }: { link: NavLink; active?: boolean }) {
  const hasChildren = !!(link.children && link.children.length);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { open, openMenu, closeMenu, dismissMenu } = useHoverMenu(wrapperRef);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        dismissMenu();
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') dismissMenu();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, dismissMenu]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        aria-current={active ? 'page' : undefined}
        className={`rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap${active ? ' nav-active' : ''}`}
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="nav-dd-group relative"
      data-open={open ? 'true' : undefined}
      onMouseEnter={openMenu}
      onMouseLeave={closeMenu}
    >
      {/* Synthetic group labels don't have their own page (no href), so they
          render as a chevron-only trigger button — clicking the label still
          opens the dropdown but doesn't navigate. Real parent pages keep the
          <a> so clicking the label navigates to that page and the chevron +
          dropdown surface the child pages on hover/focus. */}
      {link.href ? (
        <a
          href={link.href}
          aria-current={active ? 'page' : undefined}
          className={`rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap${active ? ' nav-active' : ''}`}
          aria-haspopup="true"
          aria-expanded={open}
          onFocus={openMenu}
        >
          {link.label}
          <ChevronDownIcon
            className="nav-dd-chevron w-3.5 h-3.5 transition-transform duration-200"
          />
        </a>
      ) : (
        <button
          type="button"
          onClick={() => (open ? dismissMenu() : openMenu())}
          onFocus={openMenu}
          className={`rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap bg-transparent${active ? ' nav-active' : ''}`}
          aria-haspopup="true"
          aria-expanded={open}
        >
          {link.label}
          <ChevronDownIcon
            className="nav-dd-chevron w-3.5 h-3.5 transition-transform duration-200"
          />
        </button>
      )}

      {/* CSS :hover on .nav-dd-group enables pointer-events on this panel in
          the same frame as the trigger hover — no React paint gap. pt-2 is
          a hoverable bridge so a vertical move into the list never leaves
          the group. */}
      <div className="nav-dd-panel absolute left-0 top-full z-[70] pt-2">
        <div
          className="nav-more-dropdown border border-[var(--vibe-color-border)] shadow-lg min-w-[200px]"
          style={{ backgroundColor: 'var(--vibe-color-background)' }}
        >
          {link.children!.map((child) => (
            <a
              key={child.href || child.label}
              href={child.href}
              className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors whitespace-nowrap"
            >
              {child.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks, currentPath }: { navLinks: NavLink[]; currentPath?: string }) {
  const current = normalizePath(currentPath);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const moreRef = useRef<HTMLDivElement>(null);
  const { open: moreOpen, openMenu: openMore, closeMenu: closeMore, dismissMenu: dismissMore } =
    useHoverMenu(moreRef);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        dismissMore();
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen, dismissMore]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="header-measure-row flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href || link.label}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation.
          Links are centered in the remaining row (logo left, matching
          spacer right) so the nav bar reads as centered in the viewport.
          The row is vertically centered on the logo image's geometric
          middle, but the community logo typically carries an icon/crest
          above the wordmark (e.g. the maple leaf), which lifts that middle
          above the wordmark's optical center. `lg:translate-y-[4px]` drops
          the links a few px so they read as aligned with the wordmark
          rather than the image's true center. Purely optical — no layout
          height change (translate), so overflow measurement is unaffected. */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-center gap-5 xl:gap-7 flex-1 min-w-0 lg:translate-y-[4px]"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink
            key={link.href || link.label}
            link={link}
            active={
              (!!link.href && normalizePath(link.href) === current) ||
              !!link.children?.some((c) => !!c.href && normalizePath(c.href) === current)
            }
          />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="nav-dd-group relative"
            data-open={moreOpen ? 'true' : undefined}
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={openMore}
            onMouseLeave={closeMore}
          >
            <button
              onClick={() => (moreOpen ? dismissMore() : openMore())}
              onFocus={openMore}
              className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className="nav-dd-chevron w-3.5 h-3.5 transition-transform duration-200" />
            </button>

            {/* CSS :hover bridge matches DesktopNavLink — pointer-events
                enable in the same frame as the trigger hover. */}
            <div className="nav-dd-panel absolute right-0 top-full z-[70] pt-2">
              <div
                className="nav-more-dropdown border border-[var(--vibe-color-border)] shadow-lg min-w-[200px]"
                style={{ backgroundColor: 'var(--vibe-color-background)' }}
              >
                {overflowLinks.map((link) => (
                  <React.Fragment key={link.href || link.label}>
                    {link.href ? (
                      <a
                        href={link.href}
                        className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors whitespace-nowrap"
                      >
                        {link.label}
                      </a>
                    ) : (
                      // Synthetic group labels have no page of their own; keep
                      // them as an unlinked heading so the child rows below
                      // still read as grouped under that label when the group
                      // is pushed into overflow.
                      <div className="block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] whitespace-nowrap">
                        {link.label}
                      </div>
                    )}
                    {link.children?.map((child) => (
                      <a
                        key={child.href || child.label}
                        href={child.href}
                        // Indent child rows so the More dropdown reads as a
                        // nested list when a parent (e.g. Find Your Home) gets
                        // pushed into overflow and we want its sub-items to
                        // remain reachable from the dropdown.
                        className="rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text-muted)] transition-colors whitespace-nowrap"
                      >
                        {child.label}
                      </a>
                    ))}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  // Pick the more readable text color for the transparent nav when it sits on
  // top of `HeroSolidBG` (which paints the section in `--vibe-color-primary`).
  // Uses the same `getContrastTextColor` helper the Footer uses (and that the
  // tokens runtime uses to compute `--vibe-color-text-on-primary`), so the
  // nav, the hero CTA, and the footer all pick the same contrast side on a
  // given primary.
  //
  // The earlier implementation here did `pickHighestContrast(primary,
  // [textOnPrimary, textOnSecondary])`, which was technically WCAG-optimal
  // but produced black text on medium-tone primaries (gold, tan, dusty
  // blue): on a primary with luminance ~0.3, dark-on-medium scores ~5:1 vs.
  // white-on-medium ~3:1, so the picker chose dark — disagreeing with the
  // runtime's own text-on-primary token and rendering muddy black nav links
  // on what should be a white-text design. The shared helper applies the
  // documented 0.55 threshold that favors white on those medium tones.
  // The interior/hyperlocal page heroes (HeroSolidBG / HyperlocalHero) are
  // overridden in this theme to paint a solid BLACK scrim over their photo
  // background (see src/components/HeroSolidBG.tsx + HyperlocalHero.tsx),
  // rather than the primary brand color. That makes those heroes reliably
  // dark regardless of the property's primary, so the transparent nav over
  // them should read WHITE — matching the photographic home Hero — instead
  // of the old contrast-against-primary pick (which would go dark, and thus
  // illegible, on a light primary). We keep the primary-contrast branch only
  // as a defensive fallback; it no longer applies while overSolidHero is true.
  const tokens = useTokens();
  const navOverHeroColor = useMemo(() => {
    if (overSolidHero) return '#ffffff';
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens, overSolidHero]);

  // Contrasting text/icon color for elements painted on the SECONDARY brand
  // color — the desktop utility bar (phone, Apply Now, Resident Access) and the
  // secondary-filled Apply / Resident buttons in the mobile drawer. We compute
  // this directly from the resolved `secondary` token rather than trusting the
  // `--vibe-color-text-on-secondary` token: the tokens runtime only recomputes
  // `textOnSecondary` for contrast when the property explicitly sets a secondary
  // color, so on themes/properties that inherit `secondary` from elsewhere the
  // two can drift apart (e.g. dark text left on a dark secondary). Using the
  // shared 0.55-threshold helper guarantees dark text on light secondaries and
  // light text on dark ones, matching how the nav-over-hero color is picked.
  const utilOnSecondaryColor = useMemo(() => {
    const secondary = tokens?.color?.secondary;
    if (!secondary) return null;
    return getContrastTextColor(secondary);
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav when the property has no
  // Engrain Sightmap ID/URL and no complete ApartmentSync (Repli360 /
  // RRAC) pair. Engrain wins on the page itself when present; ApartmentSync
  // is the fallback. We still require BOTH ApartmentSync IDs because a
  // property with only one populated is a half-configured state (e.g.
  // Centre Point). The fields are custom property fields (typed as `text`)
  // so we read them via `useProperty()` rather than `usePropertyField`.
  const propertyForLeasing = useProperty() as Record<string, unknown>;

  // Editor-configurable override. The `header.interactive_map_show_if_exists`
  // token can be bound to a property field (Field mode) or given a literal
  // string (Custom mode). Blank by default. ADDITIVE to the Engrain /
  // ApartmentSync detection above.
  const interactiveMapShowIfExists = useContentToken('header.interactive_map_show_if_exists');
  const hasInteractiveMapOverride = !!(
    interactiveMapShowIfExists && String(interactiveMapShowIfExists).trim()
  );

  const hasInteractiveLeasing =
    hasEngrainSitemap(propertyForLeasing) ||
    hasApartmentSyncMap(propertyForLeasing) ||
    hasInteractiveMapOverride;

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens — prefer the editable token (defaults to call tracking),
  // then usePropertyPhone() which falls back to property_phone.
  const phoneToken = useContentToken('header.phone');
  const trackedPhone = usePropertyPhone();
  const phone = phoneToken || trackedPhone || '(555) 555-5555';

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // Logo color choice mirrors the nav text color logic:
  //   • Scrolled (solid white --vibe-color-background) → default to
  //     mono_black so the logo always reads against the white header bar.
  //     Falls back to whatever the editor picked (logoRaw.image, typically
  //     the primary full-color logo) if the property has no mono_black
  //     uploaded.
  //   • Transparent over HeroSolidBG (interior page) → match the picked
  //     on-color token: light text → mono_white, dark text → mono_black.
  //   • Transparent over photographic Hero (home) → keep mono_white as
  //     before (white reads against the dark gradient overlay).
  const wantsDarkOnHero = !!navOverHeroColor && relativeLuminance(navOverHeroColor) < 0.5;
  const logoImage = scrolled
    ? (monoBlackLogo || logoRaw.image)
    : overSolidHero && wantsDarkOnHero
      ? (monoBlackLogo || logoRaw.image)
      : (monoWhiteLogo || logoRaw.image);
  const logo = { ...logoRaw, image: logoImage };
  // The parent runtime types the logo slot group's resolved value as only
  // `{ image }`, so read the `alt` field (declared on logoSlotDef) through a
  // narrowed cast for both the header-bar and drawer logos.
  const logoAlt = (logoRaw as { alt?: string }).alt;

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const qualifyingCriteriaLink = useSlotGroup('header.qualifying_criteria_link', qualifyingCriteriaLinkDef);
  // WA-only: Qualifying Criteria is a Washington disclosure page. Keep the
  // slot group mounted (schema / editor) but hide the CTA everywhere else.
  const propertyState = usePropertyField('property_address_state');
  const showQualifyingCriteria = isWashingtonState(propertyState);
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Two-line split for the drawer's Visit Us block: street on line 1, the
  // city / state / zip on line 2 (country segment dropped).
  const { line1: addressLine1, line2: addressLine2 } = splitAddressLines(addressLink.address);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();

  // Property name — fallback wordmark in the drawer when no white logo exists.
  const propertyName = usePropertyName();

  // Office hours for the mobile drawer, read from the same `office_hours`
  // collection the Footer uses and grouped into day ranges.
  const officeHours = useCollectionBinding<OfficeHour>('header.office_hours', {
    collection: 'office_hours',
  });
  const groupedHours = officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  // Current page path for active-link styling in the drawer.
  const currentPath = normalizePath(
    pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined),
  );

  // White logo for the dark drawer (independent of the header bar's
  // scroll / over-hero logo swap above).
  const mobileLogoImage = monoWhiteLogo || logoRaw.image;

  // Google Fonts — Montserrat headings, Poppins body
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="weidner-brand"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href =
      'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&family=Poppins:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'weidner-brand');
    document.head.appendChild(link);
  }, []);

  // Lock body scroll when mobile menu is open. Also drop any leftover
  // pointer-events lock on <body>/<html> (a captured pointer or a modal
  // that unmounted mid-navigation can leave the page unclickable until
  // reload) whenever the menu closes or the document is leaving.
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  useEffect(() => {
    const unlockPointer = () => {
      document.body.style.removeProperty('pointer-events');
      document.documentElement.style.removeProperty('pointer-events');
    };
    const onLeave = () => {
      document.body.style.overflow = '';
      unlockPointer();
      setMenuOpen(false);
    };
    const onNavClick = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('a[href]')) return;
      unlockPointer();
    };
    window.addEventListener('pagehide', onLeave);
    window.addEventListener('pageshow', unlockPointer);
    window.addEventListener('popstate', onLeave);
    document.addEventListener('click', onNavClick, true);
    return () => {
      window.removeEventListener('pagehide', onLeave);
      window.removeEventListener('pageshow', unlockPointer);
      window.removeEventListener('popstate', onLeave);
      document.removeEventListener('click', onNavClick, true);
      document.body.style.overflow = '';
      unlockPointer();
    };
  }, []);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 w-full transition-all duration-500 ease-in-out${menuOpen ? ' z-[110]' : ' z-50'}${scrolled ? ' header-scrolled' : ' bg-transparent header-transparent'}${overSolidHero ? ' header-over-solid-hero' : ''}`}
        style={{
          top: 0,
          transition: 'background-color 0.5s ease',
          backgroundColor: scrolled ? 'var(--vibe-color-background)' : undefined,
          // Luminance-picked text color for the transparent nav over a
          // `HeroSolidBG` (primary-color) section. Falls back to white via the
          // CSS rules below when this var is absent (e.g. home page with the
          // photographic hero, or before tokens have hydrated).
          ...(navOverHeroColor ? { ['--rise-nav-over-hero-text' as string]: navOverHeroColor } : {}),
          // Contrast-picked text/icon color for the secondary-colored utility
          // bar + drawer CTAs. Falls back to the text-on-secondary token below
          // when tokens haven't hydrated yet.
          ...(utilOnSecondaryColor ? { ['--rise-util-on-secondary' as string]: utilOnSecondaryColor } : {}),
        }}
      >
        {/* Editor-only control. Extra nav-visibility override beyond the
            automatic Engrain Sightmap ID / ApartmentSync detection.
            Rendered visually hidden because it's a configuration value, not
            display copy, but wrapped in EditableToken so it's reachable from
            the editor sidebar / inline editor and satisfies the schema linter. */}
        <EditableToken
          path="header.interactive_map_show_if_exists"
          type="text"
          label="Interactive Map: Show If Exists"
          description="Optional extra signal to expose the Interactive Map page. Engrain Sightmap IDs and complete ApartmentSync configs already show the page automatically. Leave blank to rely on those."
          as="span"
          className="sr-only"
        >
          {interactiveMapShowIfExists || ''}
        </EditableToken>

        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Sits between the SpecialOfferBanner
              (which adds its height to the header-bar's paddingTop above)
              and the main nav row below. Left: address (Maps link) + phone.
              Right: Apply Now, Resident Access, and (WA only) Qualifying
              Criteria. The links remain available on mobile via the drawer
              further down — we don't surface a second row on small screens
              because vertical space matters more there. `header-util-row`
              paints a solid brand strip so it reads as a distinct bar above
              the transparent main nav (see the <style> block). */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between gap-6 h-9">
                {/* Left — address + phone */}
                <div className="flex items-center gap-6 min-w-0">
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-link header-util-address"
                      aria-label="View property on Google Maps"
                    >
                      {addressLink.address || '123 Main Street, Atlanta, GA 30313'}
                    </a>
                  </EditableSlotGroup>

                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref header-util-link"
                    aria-label="Call us"
                  >
                    <EditableToken
                      path="header.phone"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>
                </div>

                {/* Right — Apply Now, Resident Access, Qualifying Criteria (WA) */}
                <div className="flex items-center gap-6 flex-shrink-0">
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="header-util-link"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-link"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>

                  <div className={showQualifyingCriteria ? undefined : 'hidden'} aria-hidden={!showQualifyingCriteria}>
                    <EditableSlotGroup groupPath="header.qualifying_criteria_link" definition={qualifyingCriteriaLinkDef}>
                      <a
                        href={qualifyingCriteriaLink.url || '/qualifying-criteria'}
                        target={qualifyingCriteriaLink._target || '_self'}
                        className="header-util-link"
                        tabIndex={showQualifyingCriteria ? undefined : -1}
                      >
                        {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
                      </a>
                    </EditableSlotGroup>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8">
            {/* Mobile: hamburger left · logo centered · phone right.
                Tablet (md → lg): hamburger left · logo centered · Schedule
                a Tour CTA right.
                Desktop (lg+): logo left · centered nav links · matching
                Schedule a Tour column so the link cluster sits on the
                viewport midline. */}
            <div className="relative flex items-center h-24 lg:h-28">
              {/* Hamburger — mobile/tablet only, pinned left */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="lg:hidden relative w-10 h-10 flex items-center justify-center flex-shrink-0"
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'rotate-45' : '-translate-y-2'
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                    menuOpen ? '-rotate-45' : 'translate-y-2'
                  }`}
                />
              </button>

              {/* Logo — centered on mobile/tablet; on desktop sits in the
                  flex row with the nav so both share the same vertical
                  center (parent `items-center`). Fixed `lg:w-[220px]`
                  matches the trailing Schedule a Tour column so the nav
                  cluster is optically centered. Absolute positioning lives
                  on the slot-group wrapper (not the <a>) so the in-flow
                  desktop size is correct and the image baseline gap can't
                  nudge the mark off the nav midline. */}
              <EditableSlotGroup
                groupPath="header.logo"
                definition={logoSlotDef}
                className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 lg:static lg:z-auto lg:translate-x-0 lg:translate-y-0 lg:flex lg:w-[220px] lg:flex-shrink-0 lg:items-center"
              >
                <a
                  href="/"
                  className="flex flex-shrink-0 items-center"
                >
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logoAlt || 'Community Logo'}
                      sizes="240px"
                      className="block h-20 w-auto max-w-[220px] object-contain lg:h-24"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      The Rise
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links — centered between the logo and a
                  matching-width Schedule a Tour column so the cluster sits
                  on the viewport midline. Active-link detection prefers the
                  runtime-resolved page path (usePage) over window.location:
                  in the editor/preview the iframe URL often isn't the
                  page's real path, so the latter would never match a nav
                  href. Falls back to the live pathname only when the
                  runtime path is absent. */}
              <DesktopNav
                navLinks={navLinks}
                currentPath={pageCtx?.path ?? (typeof window !== 'undefined' ? window.location.pathname : undefined)}
              />

              {/* Phone icon — mobile (phone-width) only, pinned right to
                  balance the hamburger. On tablet and desktop it's replaced
                  by the Schedule a Tour CTA below; desktop phone also lives
                  in the utility bar. */}
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref md:hidden ml-auto w-10 h-10 flex items-center justify-center flex-shrink-0 text-[var(--vibe-color-text)]"
                aria-label="Call us"
              >
                <PhoneIcon className="w-5 h-5" />
              </a>

              {/* Schedule a Tour CTA — tablet and desktop (md+). On tablet
                  it stands in for the phone icon (`ml-auto` against the
                  absolutely-centered logo). On desktop it fills the
                  right-hand 220px column (matching the logo) so the nav
                  cluster stays optically centered and the button sits on
                  the right edge of the navigation bar. Painted on the
                  secondary brand color with the same contrast-picked
                  on-secondary text as the utility-bar CTAs so it reads on
                  both the transparent (over-hero) and scrolled (solid
                  white) header states. */}
              <EditableSlotGroup
                groupPath="header.schedule_tour_link"
                definition={scheduleTourLinkDef}
                className="hidden md:flex ml-auto flex-shrink-0 lg:ml-0 lg:w-[220px] lg:justify-end lg:items-center"
              >
                <a
                  href={scheduleTourLink.url || '/schedule-a-tour'}
                  target={scheduleTourLink._target || '_self'}
                  className="header-tour-cta inline-flex items-center justify-center px-5 py-2.5 text-xs font-semibold tracking-[0.15em] uppercase whitespace-nowrap bg-[var(--vibe-color-secondary)]"
                  style={{ color: 'var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary))' }}
                >
                  {scheduleTourLink.label || 'Schedule a Tour'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu — full-height primary-color left drawer over a      */}
        {/* dimmed backdrop. Accordion nav + filled secondary CTAs match   */}
        {/* the Weidner mobile reference (X + MENU chrome, left-aligned).  */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-backdrop lg:hidden fixed inset-0 z-[60] bg-black/60 transition-opacity duration-300 ${
            menuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
          }`}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />

        <div
          className={`mobile-menu-panel lg:hidden fixed inset-y-0 left-0 z-[61] flex flex-col w-[88%] max-w-[420px] overflow-y-auto transition-transform duration-300 ease-out ${
            menuOpen ? 'translate-x-0' : '-translate-x-full pointer-events-none'
          }`}
          style={{ backgroundColor: 'var(--vibe-color-primary)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
        >
          <div className="flex min-h-full flex-col px-7 pt-6 pb-10">
            {/* Top chrome — close (X) */}
            <div className="flex items-center flex-shrink-0">
              <button
                onClick={closeMobileMenu}
                aria-label="Close menu"
                className="w-10 h-10 flex items-center justify-center text-[var(--vibe-color-text-on-primary)] hover:opacity-80 transition-opacity"
              >
                <CloseIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Left-aligned logo — matches the nav link column below */}
            <a href="/" onClick={closeMobileMenu} className="flex justify-start mt-8 mb-10">
              {mobileLogoImage ? (
                <VibesitesImage
                  url={mobileLogoImage}
                  alt={logoAlt || 'Community Logo'}
                  sizes="220px"
                  className="h-24 w-auto max-w-[220px] object-contain"
                />
              ) : (
                <span className="font-heading text-2xl font-bold uppercase tracking-wide text-[var(--vibe-color-text-on-primary)]">
                  {propertyName || 'Our Community'}
                </span>
              )}
            </a>

            {/* Left-aligned accordion navigation */}
            <nav className="flex flex-col items-stretch text-left" role="navigation" aria-label="Mobile navigation">
              {navLinks.map((link) => (
                <MobileNavItem
                  key={link.href || link.label}
                  link={link}
                  currentPath={currentPath}
                  onNavigate={closeMobileMenu}
                />
              ))}
            </nav>

            {/* Filled secondary CTAs */}
            <div className="pt-10 space-y-3">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="mobile-drawer-btn block text-center w-full py-4 text-xs font-semibold tracking-[0.2em] uppercase bg-[var(--vibe-color-secondary)]"
                  style={{ color: 'var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary))' }}
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  onClick={closeMobileMenu}
                  className="mobile-drawer-btn block text-center w-full py-4 text-xs font-semibold tracking-[0.2em] uppercase bg-[var(--vibe-color-secondary)]"
                  style={{ color: 'var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary))' }}
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
              <div className={showQualifyingCriteria ? undefined : 'hidden'} aria-hidden={!showQualifyingCriteria}>
                <EditableSlotGroup groupPath="header.qualifying_criteria_link" definition={qualifyingCriteriaLinkDef}>
                  <a
                    href={qualifyingCriteriaLink.url || '/qualifying-criteria'}
                    target={qualifyingCriteriaLink._target || '_self'}
                    onClick={closeMobileMenu}
                    className="mobile-drawer-btn block text-center w-full py-4 text-xs font-semibold tracking-[0.2em] uppercase bg-[var(--vibe-color-secondary)]"
                    style={{ color: 'var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary))' }}
                    tabIndex={showQualifyingCriteria ? undefined : -1}
                  >
                    {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
                  </a>
                </EditableSlotGroup>
              </div>
            </div>

            {/* Visit Us — address + phone */}
            <div className="pt-10 text-left">
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--vibe-color-text-on-primary)] mb-3">Visit Us</h4>
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="block text-sm leading-relaxed text-[var(--vibe-color-text-on-primary)]/90 hover:opacity-80 transition-opacity"
                >
                  <span className="block">{addressLine1}</span>
                  {addressLine2 && <span className="block">{addressLine2}</span>}
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref block mt-4 text-base text-[var(--vibe-color-text-on-primary)]/90 hover:opacity-80 transition-opacity"
              >
                <EditableToken
                  path="header.phone"
                  as="span"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>

            {/* Office Hours */}
            <div className="pt-8 mt-auto text-left">
              <h4 className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--vibe-color-text-on-primary)] mb-3">Office Hours</h4>
              {groupedHours.length > 0 ? (
                <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm text-[var(--vibe-color-text-on-primary)]/90 w-fit">
                  {groupedHours.map((group, index) => (
                    <React.Fragment key={index}>
                      <span className="whitespace-nowrap">{group.shortLabel}</span>
                      <span className="text-right whitespace-nowrap">{group.hours}</span>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[var(--vibe-color-text-on-primary)]/50">Office hours will be displayed here.</p>
              )}
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. Hover keeps the text color steady (still
           !important to override the parent .header-transparent .header-bar
           nav a:hover white rule) and uses the same secondary double-line
           border treatment as the top-level nav (see below).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Mobile drawer — primary brand fill with on-primary text. Active
           links use secondary (gold/tan) to match the Weidner reference. */
        .mobile-menu-panel {
          color: var(--vibe-color-text-on-primary);
        }
        .mobile-menu-panel .mobile-nav-link {
          color: var(--vibe-color-text-on-primary);
          font-family: var(--vibe-font-body);
        }
        .mobile-menu-panel .mobile-nav-link.mobile-nav-active {
          color: var(--vibe-color-secondary);
        }
        .mobile-menu-panel .mobile-nav-child:not(.mobile-nav-active) {
          color: color-mix(in srgb, var(--vibe-color-text-on-primary) 72%, transparent);
        }
        .mobile-menu-panel .mobile-nav-toggle {
          cursor: pointer;
        }

        /* Nav links previously drew an animated underline on hover (the
           "white bottom underline"). That hover underline has been removed
           per request — links keep only the color-shift hover affordance
           from the !important rules above. The .rise-nav-darken-hover class
           is still present in markup as a styling hook but draws no
           underline; we keep the color transition so the brightening hover
           stays smooth. */
        .rise-nav-darken-hover {
          transition: color 0.2s ease;
        }
        /* Desktop dropdowns: CSS :hover / :focus-within enables the panel
           (and its pointer-events) in the same frame as the trigger hover.
           JS data-open covers click/touch. A delayed hide lets the pointer
           cross the trigger→panel bridge without the menu collapsing. */
        .nav-dd-panel {
          min-width: 100%;
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transform: translateY(-4px);
          transition:
            opacity 0.2s ease,
            transform 0.2s ease,
            visibility 0s linear 0.2s,
            pointer-events 0s linear 0.2s;
        }
        .nav-dd-group:hover .nav-dd-panel,
        .nav-dd-group:focus-within .nav-dd-panel,
        .nav-dd-group[data-open="true"] .nav-dd-panel {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transform: translateY(0);
          transition-delay: 0s;
        }
        /* Overlap the trigger so a vertical path into the list never leaves
           the hover group, even if the pointer skips the pt-2 padding. */
        .nav-dd-panel::before {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: 100%;
          height: 16px;
        }
        .nav-dd-group:hover .nav-dd-chevron,
        .nav-dd-group:focus-within .nav-dd-chevron,
        .nav-dd-group[data-open="true"] .nav-dd-chevron {
          transform: rotate(180deg);
        }

        /* Reserve the 2px top/bottom rules on every nav trigger so hovering
           (or the active-page treatment) only changes color — it must not
           grow the trigger and shove the dropdown down under the pointer. */
        .header-bar nav a,
        .header-bar nav button {
          border-top: 2px solid transparent;
          border-bottom: 2px solid transparent;
        }
        /* Active page link — persistent brand-color rules ABOVE and BELOW
           the current page's nav item (e.g. "Home" on the home page). Drawn
           as top/bottom borders in --vibe-color-secondary so the accent reads
           against both the white scrolled header and the dark hero overlay,
           independent of the link's own text color. We use borders (rather
           than text-decoration) precisely because text-decoration can only
           draw on one edge — the design calls for a line top and bottom. The
           link already carries py-2, which spaces the rules off the text;
           the header row is fixed-height + items-center, so the extra 2px
           edges don't shift the row. */
        .header-bar nav a.nav-active,
        .header-bar nav button.nav-active {
          border-top: 2px solid var(--vibe-color-secondary);
          border-bottom: 2px solid var(--vibe-color-secondary);
        }
        /* Same double-line (top + bottom secondary-color rules) on hover of a
           main nav link, so hovering previews the active-page treatment. The
           row is fixed-height + items-center, so the 2px edges don't shift it.
           Dropdown panel links (Features, Gallery & Tours, More overflow) use
           the same secondary double-line hover for congruence; they reserve
           transparent borders by default so the stacked list doesn't reflow
           when the secondary color appears. */
        .header-bar nav a:hover,
        .header-bar nav button:hover {
          border-top: 2px solid var(--vibe-color-secondary);
          border-bottom: 2px solid var(--vibe-color-secondary);
        }
        .header-bar nav .nav-more-dropdown a {
          border-top: 2px solid transparent;
          border-bottom: 2px solid transparent;
        }
        .header-bar nav .nav-more-dropdown a:hover {
          border-top: 2px solid var(--vibe-color-secondary);
          border-bottom: 2px solid var(--vibe-color-secondary);
        }
        /* Filled secondary CTAs in the drawer — slight darken on hover. */
        .mobile-menu-panel .mobile-drawer-btn {
          transition: filter 0.2s ease, opacity 0.2s ease;
        }
        .mobile-menu-panel .mobile-drawer-btn:hover {
          filter: brightness(0.95);
        }

        /* Transparent header: hamburger bars + phone icon → white.
           Scoped with :not(.header-util-link) so the phone that lives in the
           solid secondary utility bar is NOT forced white — it keeps the
           contrast-picked --rise-util-on-secondary color shared with the
           Apply Now / Resident Access links beside it. Only the phone icon
           that actually overlays the transparent nav / hero flips to white. */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }
        .header-transparent a.dniphone:not(.header-util-link) {
          color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars, phone icon + site-name fallback
           also flip to the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero a.dniphone:not(.header-util-link) {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — solid primary (brand) bar above the main nav row. */
        /* ----------------------------------------------------------------- */
        /* Painted in --vibe-color-secondary in every header state (transparent,
           scrolled, over-solid-hero) so the brand strip stays constant while
           the nav row below transitions transparent → solid on scroll. Its
           links read in --vibe-color-text-on-secondary (see below). */
        .header-util-row {
          background-color: var(--vibe-color-secondary);
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          /* Contrast-picked against the secondary bar background (see
             --rise-util-on-secondary in the component); falls back to the
             text-on-secondary token before tokens hydrate. */
          color: color-mix(in srgb, var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary)) 88%, transparent);
          white-space: nowrap;
          transition: color 0.2s ease;
        }
        /* On the solid secondary bar the links simply brighten to the full
           contrast color on hover — no underline affordance. */
        .header-util-link:hover {
          color: var(--rise-util-on-secondary, var(--vibe-color-text-on-secondary));
        }
        /* Address keeps sentence case — uppercase + wide tracking makes a
           full street address hard to scan in the narrow util strip. */
        .header-util-link.header-util-address {
          text-transform: none;
          letter-spacing: 0.02em;
          font-weight: 500;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Tablet + desktop Schedule a Tour button — filled secondary brand
           CTA on the right of the nav bar (replaces the phone icon from md
           up). Slight darken on hover mirrors the mobile drawer CTAs. */
        .header-tour-cta {
          transition: filter 0.2s ease;
        }
        .header-tour-cta:hover {
          filter: brightness(0.95);
        }

        /* Nav uses body token (Poppins). Poppins has 600, so font-semibold is fine. */
        .header-bar nav a,
        .header-bar nav button,
        .header-measure-row span,
        .header-util-link,
        .header-tour-cta,
        .mobile-menu-panel .mobile-drawer-btn {
          font-family: var(--vibe-font-body);
        }

        /* Remap leftover hardcoded Space Mono / Oswald / system mono */
        html body .ts-heading,
        html body .faq-heading-font,
        html body .faq-heading-font *,
        html body .amenities-list-heading,
        html body .amenities-list-heading *,
        html body .amenities-summary-heading,
        html body .amenities-summary-heading *,
        html body .ppi-hero-title-font,
        html body .ppi-hero-title-font *,
        html body .pet-cta,
        html body .lc-heading,
        html body .lc-form-panel .mh-lead-form-submit-button {
          font-family: var(--vibe-font-heading) !important;
        }

        html body .ts-quote,
        html body .ts-readmore,
        html body .ts-author,
        html body .ts-count,
        html body .lc-body,
        html body .lc-form-empty,
        html body .lc-form-panel input,
        html body .lc-form-panel select,
        html body .lc-form-panel textarea,
        html body .overview-description-nav-font,
        html body .overview-description-nav-font *,
        html body .overview-nav-font,
        html body .overview-nav-font *,
        html body .fpi-pricing,
        html body .fpi-pricing *,
        html body .amenities-list-item,
        html body .amenities-summary-item,
        html body .ppi-hero-blurb-font,
        html body .ppi-hero-blurb-font * {
          font-family: var(--vibe-font-body) !important;
        }

        /* ================================================================ */
        /* Richtext highlight neutralization (theme-wide)                  */
        /* ----------------------------------------------------------------- */
        /* Browser UA styles paint <mark> yellow, and TipTap Highlight /
           pasted Word/Docs HTML often leave background-color on spans.
           That shows up as a highlighter bar behind body copy on pages
           whose content carries those marks. Clear backgrounds only —
           do not reset color on spans (TipTap Color / text-color marks
           reuse data-color for intentional text tint). */
        /* Strip leftover TipTap / pasted font-family marks so richtext
           inherits the wrapper face (heading or body token).
           display:contents on the richtext span keeps children
           addressable as descendants. */
        [data-vibesite-richtext] * {
          font-family: inherit !important;
        }
        [data-vibesite-richtext] mark {
          background: transparent !important;
          background-color: transparent !important;
          color: inherit;
        }
        [data-vibesite-richtext] [style*="background-color"],
        [data-vibesite-richtext] [style*="background:"] {
          background-color: transparent !important;
          background-image: none !important;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    'header.interactive_map_show_if_exists': {
      label: 'Interactive Map: Show If Exists',
      type: 'text',
      description:
        'Optional extra signal to expose the Interactive Map page. Engrain Sightmap IDs and complete ApartmentSync configs already show the page automatically. Leave blank to rely on those.',
      defaultValue: '',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.qualifying_criteria_link': {
      label: 'Qualifying Criteria Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/qualifying-criteria' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule a Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
    'header.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
